import React from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const VARIANT: Record<ButtonVariant, string> = {
  primary:
    'bg-primary-500 text-white shadow-card hover:bg-primary-600 active:bg-primary-700 ' +
    'disabled:bg-slate-300 disabled:text-white',
  secondary:
    'bg-white text-slate-800 border border-slate-300 shadow-card hover:bg-slate-50 ' +
    'active:bg-slate-100 disabled:text-slate-400 disabled:bg-slate-50',
  ghost:
    'bg-transparent text-slate-700 hover:bg-slate-100 active:bg-slate-200 ' +
    'disabled:text-slate-400',
  danger:
    'bg-danger-500 text-white shadow-card hover:bg-danger-600 active:bg-danger-700 ' +
    'disabled:bg-slate-300',
};

const SIZE: Record<ButtonSize, string> = {
  sm: 'h-7 px-2.5 text-xs gap-1.5',
  md: 'h-9 px-3.5 text-sm gap-2',
  lg: 'h-11 px-5 text-base gap-2',
};

/**
 * Standard button. Atlassian-style fills with Apple-soft shadow + 120ms
 * transitions. Composes via `className` so per-call tweaks still work.
 */
export const Button: React.FC<ButtonProps> = ({
  variant = 'secondary',
  size = 'md',
  loading,
  leftIcon,
  rightIcon,
  className = '',
  disabled,
  children,
  ...rest
}) => {
  const isDisabled = disabled || loading;
  return (
    <button
      {...rest}
      disabled={isDisabled}
      className={
        'inline-flex items-center justify-center rounded-md font-medium ' +
        'transition-colors duration-150 select-none ' +
        'disabled:cursor-not-allowed disabled:opacity-100 ' +
        VARIANT[variant] + ' ' + SIZE[size] + ' ' + className
      }
    >
      {loading && (
        <span
          aria-hidden
          className="inline-block h-3.5 w-3.5 rounded-full border-2 border-current border-t-transparent animate-spin"
        />
      )}
      {!loading && leftIcon}
      <span>{children}</span>
      {!loading && rightIcon}
    </button>
  );
};

export default Button;
